import { useMemo, useState } from 'react';

import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  VOICE_SCORE_KEYS,
  VOICE_SCORE_LABELS,
  type VoiceHistoryDay,
  type VoiceScoreKey,
} from '@/lib/api/voice-history';

interface Props {
  days: VoiceHistoryDay[];
}

interface SeriesDef {
  key: VoiceScoreKey;
  label: string;
  color: string;
}

const SERIES: SeriesDef[] = [
  { key: 'hearingScore', label: VOICE_SCORE_LABELS.hearingScore, color: '#1976D2' },
  { key: 'cognitiveScore', label: VOICE_SCORE_LABELS.cognitiveScore, color: '#2E7D32' },
  { key: 'stressScore', label: VOICE_SCORE_LABELS.stressScore, color: '#ED6C02' },
];

/**
 * 보이스웰니스 기간 추이 (BgtHistoryChart와 동일 UX, 평균선 없음)
 */
export function VoiceHistoryChart({ days }: Props) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const data = useMemo(
    () =>
      days.map((d) => {
        const row: Record<string, number | string | null> = { date: d.date };
        for (const key of VOICE_SCORE_KEYS) {
          row[key] = d.hasData ? d[key] : null;
        }
        return row;
      }),
    [days],
  );

  const hasAnyActive = days.some((d) => d.hasData);

  if (!hasAnyActive) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            점수 추이
          </Typography>
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.disabled">
              선택한 구간에 보이스웰니스 기록이 없습니다.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const toggleSeries = (key: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            점수 추이
          </Typography>
          <Typography variant="caption" color="text.disabled">
            범례 클릭으로 토글 · 빈 칸은 미사용일
          </Typography>
        </Stack>
        <Box sx={{ width: '100%', height: 340 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, name) => {
                  const v = value as number | null | undefined;
                  return [v === null || v === undefined ? '—' : `${v}점`, name as string];
                }}
              />
              <Legend
                onClick={(e) => toggleSeries((e as { dataKey?: string }).dataKey ?? '')}
                wrapperStyle={{ cursor: 'pointer', fontSize: 12 }}
              />
              {SERIES.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={1.8}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  hide={hidden.has(s.key)}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}
